import type { FC } from 'react';
import { useCallback, useContext } from 'react';
import classnames from 'classnames';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Url } from '@wix/da-url';
import GhostButton from '@wix/da-ds/pkg/Buttons/GhostButton';
import GhostButtonWithContextMenu from '@wix/da-ds/pkg/Dropdown/Preset/GhostButtonWithContextMenu';
import Flex from '@wix/da-ds/pkg/Flex';
import DreamUpStars from '@wix/da-ds/pkg/Icons/24x24/DreamUpStars';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { ResourceType } from '@wix/da-shared-react/pkg/types/resource';
import {
  BiData,
  DreamupInfoClickBiEvent,
  EditWithMuroBiEvent,
} from '@wix/da-bi/pkg/events';
import { useFlag } from '@wix/da-react-context/pkg/flags/hooks/useFlag';
import { pushModal } from '@wix/da-shared-react/pkg/Modals/redux/basicActions';
import { ModalType } from '@wix/da-shared-react/pkg/Modals/redux/types';
import { Section } from '@wix/da-a11y/pkg/components/headings/tagWrappers';
import Heading from '@wix/da-a11y/pkg/components/headings/Heading';
import SolidButton from '@wix/da-ds/pkg/Buttons/SolidButton';
import OrderDropdown from '../../../../_partials/OrderDropdown';
import useSubmitButtonProps from '../../../../_partials/SubmitButton/useSubmitButtonProps';
import { DailyChallengeArray } from '../types';

import s from './SelectedDailyChallengeHeading.scss';

export interface Props {
  tag: string;
  tagItemId: number;
  dailyChallenges: DailyChallengeArray;
  className?: string;
}

// todo maybe we don't need the full challenge? just tag and isToday?
const SelectedDailyChallengeHeading: FC<Props> = ({
  tag,
  tagItemId,
  dailyChallenges,
  className,
}) => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);
  const dispatch = useDispatch();
  const pushMiniMuroModal = useCallback(() => {
    dispatch(pushModal(ModalType.MINI_MURO, {}));
  }, [dispatch]);
  const pushMobileMuroModal = useCallback(() => {
    dispatch(pushModal(ModalType.MINI_MOBILE_MURO, {}));
  }, [dispatch]);

  const hasMobileFullFeature = useFlag('mobile_muro_full');
  const submitButtonProps = useSubmitButtonProps({
    tags: tag,
    biSectionname: 'tags',
  });

  const isShowingMobileDreamUpCTA = isMobile;
  if (!tag || !Array.isArray(dailyChallenges)) {
    return null;
  }

  // we don't have anything from them to tell -which- challenge matched the current tag
  const { today } =
    dailyChallenges.find(challenge => challenge.tag.tag === tag) || {};

  return (
    <Section className={classnames(className, s['root'])}>
      {!isMobile && <Heading className={s['current-tag']}>{`#${tag}`}</Heading>}
      {isMobile && (
        <div className={s['mobile-heading-and-orderdropdown']}>
          <Heading className={s['current-tag']}>{`#${tag}`}</Heading>
          <OrderDropdown className={s['order-dropdown']} />
        </div>
      )}

      <p className={s['challenge-text']}>
        <Trans
          i18nKey="tagPage.dailyChallenge.challengeText"
          specifier={t(
            today
              ? 'tagPage.dailyChallenge.challengeText.themeSpecifier.today'
              : 'tagPage.dailyChallenge.challengeText.themeSpecifier.notToday'
          )}
        >
          Create and submit artwork each day using the
          <a href={t('tagPage.dailyChallenge.challengeUrl')}>Drawtober 2023</a>
          prompt list to earn Profile badges!
        </Trans>
      </p>
      <Flex className={s['submit-container']}>
        {!hasMobileFullFeature && isShowingMobileDreamUpCTA && (
          <GhostButton
            variant="main"
            size="medium"
            className={s['dreamup-cta']}
            icon={DreamUpStars}
            href={Url.dreamupLink({ prompt: tag })}
            biData={BiData<DreamupInfoClickBiEvent>({
              evid: 923,
              typeid: ResourceType.DISCOVERYTAG,
              itemid: tagItemId,
              sectionname: 'daily_challenge_header',
            })}
          >
            {t('tagPage.dailyChallenge.createWithDreamup.mobileText')}
          </GhostButton>
        )}
        {!isMobile && (
          <GhostButtonWithContextMenu
            variant="main"
            size="medium"
            className={s['dreamup-cta']}
            icon={DreamUpStars}
            items={[
              {
                label: t('tagPage.dailyChallenge.createWithDreamup.text'),
                link: Url.dreamupLink({ prompt: tag }),
                biData: BiData<DreamupInfoClickBiEvent>({
                  evid: 923,
                  typeid: ResourceType.DISCOVERYTAG,
                  itemid: tagItemId,
                  sectionname: 'daily_challenge_header',
                }),
              },
              {
                label: t('tagPage.dailyChallenge.createWithDraw.text'),
                onClick: pushMiniMuroModal,
                biData: BiData<EditWithMuroBiEvent>({
                  evid: 926,
                  page_typeid: ResourceType.DISCOVERYTAG,
                  page_itemid: tagItemId,
                  sectionname: 'daily_challenge_header',
                }),
              },
            ]}
          >
            {t('tagPage.dailyChallenge.createMenuButton.text')}
          </GhostButtonWithContextMenu>
        )}

        {hasMobileFullFeature && isMobile && (
            <GhostButtonWithContextMenu
              variant="main"
              size="medium"
              className={s['dreamup-cta']}
              icon={DreamUpStars}
              items={[
                {
                  label: t('tagPage.dailyChallenge.createWithDreamup.text'),
                  link: Url.dreamupLink({ prompt: tag }),
                  biData: BiData<DreamupInfoClickBiEvent>({
                    evid: 923,
                    typeid: ResourceType.DISCOVERYTAG,
                    itemid: tagItemId,
                    sectionname: 'daily_challenge_header',
                  }),
                },
                {
                  label: t('tagPage.dailyChallenge.createWithDraw.text'),
                  onClick: pushMobileMuroModal,
                  biData: BiData<EditWithMuroBiEvent>({
                    evid: 926,
                    page_typeid: ResourceType.DISCOVERYTAG,
                    page_itemid: tagItemId,
                    sectionname: 'daily_challenge_header',
                  }),
                },
              ]}
            >
              {t('tagPage.dailyChallenge.createMenuButton.text')}
            </GhostButtonWithContextMenu>
          )}

        {isMobile ? (
          <SolidButton
            className={s['submit-button']}
            variant="approval-green"
            size="medium"
            {...submitButtonProps}
          >
            {t('tagPage.dailyChallenge.submitYourArt')}
          </SolidButton>
        ) : (
          <SolidButton
            className={s['submit-button']}
            variant="approval-green"
            size="medium"
            {...submitButtonProps}
          >
            {t('tagPage.dailyChallenge.submitTo', { tag })}
          </SolidButton>
        )}
      </Flex>
    </Section>
  );
};
SelectedDailyChallengeHeading.displayName = 'SelectedDailyChallengeHeading';

export default SelectedDailyChallengeHeading;
